import { useCallback, useEffect, useRef } from 'react';
import { useConfirm } from '../../modal/ConfirmModal/useConfirm';
import { toasterService } from '../../toaster/toaster.service';
import { EntityActionsTrackers } from '../../pipelines/tracking/entity-action-trackers';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import { EntityActionConfirmDialogProps } from '../interfaces/entity-actions';

interface UseConfirmDialogForEntityActionsProps<A extends string | number | symbol, E> {
  entity: E;
  confirmationDialogProps?: EntityActionConfirmDialogProps<A, E>;
  updateEntity?: (entity: E) => void;
  getEntityFromActionResponse?: (res: unknown) => E;
}

export function useConfirmDialogForEntityActions<A extends string | number | symbol, E>({
  entity,
  updateEntity,
  confirmationDialogProps,
  getEntityFromActionResponse
}: UseConfirmDialogForEntityActionsProps<A, E>) {
  const { confirm } = useConfirm();
  const { eventTrack } = useAnalyticsTracker();
  const abortController = useRef(new AbortController());

  useEffect(() => {
    return () => {
      abortController.current.abort();
    };
  }, []);

  const showConfirmDialog = useCallback(
    (action: A) => {
      if (!confirmationDialogProps) {
        return;
      }

      abortController.current = new AbortController();

      const {
        actionHandler,
        successMessage,
        bodyComponentProps = {},
        ...dialogProps
      } = confirmationDialogProps[action];

      if (!dialogProps || !actionHandler) {
        return;
      }

      confirm({
        ...dialogProps,
        bodyComponentProps: {
          entity,
          action,
          ...bodyComponentProps
        },
        hideActionOnError: () => true,
        onPositiveClickCallback: extraParams => {
          eventTrack({
            action: EntityActionsTrackers.ENTITY_ACTION_CONFIRM_CLICKED,
            properties: {
              action
            }
          });

          return actionHandler({
            entity,
            signal: abortController.current.signal,
            ...(extraParams ?? {})
          }).catch(e => {
            eventTrack({
              action: EntityActionsTrackers.ENTITY_ACTION_ERRORED,
              properties: {
                action,
                errorMessage: getErrorMessageFromAxiosError(e)
              }
            });

            throw e;
          });
        }
      }).then(res => {
        if (!res) {
          eventTrack({
            action: EntityActionsTrackers.ENTITY_ACTION_CANCEL_CLICKED,
            properties: {
              action
            }
          });

          return;
        }

        eventTrack({
          action: EntityActionsTrackers.ENTITY_ACTION_SUCCESS,
          properties: {
            action
          }
        });

        if (
          typeof updateEntity === 'function' &&
          typeof getEntityFromActionResponse === 'function' &&
          typeof res === 'object' &&
          'data' in res
        ) {
          const _entity = getEntityFromActionResponse(res.data);

          updateEntity({
            ...entity,
            ..._entity
          });
        }

        if (successMessage) {
          toasterService.pop('success', undefined, successMessage);
        }
      });
    },
    [entity, updateEntity]
  );

  return { showConfirmDialog };
}
